import React from "react";
import { observer } from "mobx-react";
import { EditionUpdateStore } from "../../store";
import { EditionUpdateForm } from "./EditionUpdateForm";
import { ErrorDialog, ConfirmCancelDialog } from "../../../../common/controls";
import { SavingOverlay } from "../../../../common/controls/forms/SavingOverlay";
import { OutdatedAfterSaveNotice } from "../../../../common/controls/forms/OutdatedAfterSaveNotice";
import { MusicalWorkMiniForm } from "../../../musicalWork/components/view/MusicalWorkMiniForm";
import { EditionContentType } from "../../../../api/graphql";

export type EditionUpdateProps = {
  editionUpdateStore: EditionUpdateStore;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
};

export const EditionUpdate: React.FC<EditionUpdateProps> = observer(
  ({ editionUpdateStore, showToolbox = true, showSaveFooter }) => {
    return (
      <>
        <SavingOverlay open={editionUpdateStore.isSaving} />
        <OutdatedAfterSaveNotice
          open={editionUpdateStore.savedButOutdated}
          onReload={() => editionUpdateStore.reloadEdition()}
          reloadDisabled={editionUpdateStore.isProcessing}
        />
        <EditionUpdateForm
          editionUpdateStore={editionUpdateStore}
          documentationStateValue={
            editionUpdateStore.entityValues.showDocumentationState?.id || ""
          }
          documentationStateTransitions={
            editionUpdateStore.documentationStateTransitions
          }
          onDocumentationStateTransition={(transitionName: string) =>
            editionUpdateStore.onDocumentationStateTransition(transitionName)
          }
          documentationStateIsProcessing={
            editionUpdateStore.documentationStateTransitionProcessing
          }
          nameError={editionUpdateStore.errors.name?.message}
          onNameChange={(value: string) => {
            editionUpdateStore.setNameValue(value);
          }}
          name={editionUpdateStore.entityValues.name}
          editionTypeError={editionUpdateStore.errors.editionType?.message}
          onEditionTypeChange={(value) => {
            editionUpdateStore.setEditionTypeValue(value);
          }}
          editionTypeSelectedValue={
            editionUpdateStore.entityValues.showEditionType
          }
          onEditionTypeSelected={(value) => {
            editionUpdateStore.setSelectedEditionType(value);
          }}
          editionTypeOptions={editionUpdateStore.getEditionTypeOptions()}
          contentTypeError={editionUpdateStore.errors.contentType?.message}
          contentTypeOptions={editionUpdateStore.getContentTypeOptions()}
          onContentTypeChange={(value: EditionContentType) => {
            editionUpdateStore.setContentTypeValue(value);
          }}
          contentType={editionUpdateStore.entityValues.contentType}
          publicationLinkError={
            editionUpdateStore.errors.publicationLink?.message
          }
          onPublicationLinkChange={(value: string) => {
            editionUpdateStore.setPublicationLinkValue(value);
          }}
          publicationLink={editionUpdateStore.entityValues.publicationLink}
          priceError={editionUpdateStore.errors.price?.message}
          onPriceChange={(value: number) => {
            editionUpdateStore.setPriceValue(value);
          }}
          price={editionUpdateStore.entityValues.price}
          worksError={editionUpdateStore.errors.works?.message}
          onWorksChange={(value) => {
            editionUpdateStore.setWorksValue(value);
          }}
          worksSelectedValues={editionUpdateStore.entityValues.showWorks}
          onWorksSelected={(value) => {
            editionUpdateStore.setSelectedWorks(value);
          }}
          worksOptions={editionUpdateStore.getWorksOptions()}
          worksOptionsMap={(option: any, backgroundColor: string) => (
            <MusicalWorkMiniForm
              data={option}
              backgroundColor={backgroundColor}
              visibleFields={["title", "authors"]}
            />
          )}
          worksMiniFormMode="tooltip"
          notesError={editionUpdateStore.errors.notes?.message}
          onNotesChange={(value: string) => {
            editionUpdateStore.setNotesValue(value);
          }}
          notes={editionUpdateStore.entityValues.notes}
          contractObservationError={
            editionUpdateStore.errors.contractObservation?.message
          }
          onContractObservationChange={(value: string) => {
            editionUpdateStore.setContractObservationValue(value);
          }}
          contractObservation={
            editionUpdateStore.entityValues.contractObservation
          }
          documentationVisibleColumns={
            editionUpdateStore.documentationVisibleColumns
          }
          documentationRows={editionUpdateStore.entityValues.showDocumentation}
          associatedDocumentsVisibleColumns={
            editionUpdateStore.associatedDocumentsVisibleColumns
          }
          associatedDocumentsRows={
            editionUpdateStore.entityValues.showAssociatedDocuments
          }
          userAttachmentEditionUpdateStore={
            editionUpdateStore.userAttachmentsUpdateStore
          }
          userAttachmentsVisibleColumns={
            editionUpdateStore.userAttachmentsVisibleColumns
          }
          userAttachmentsColumnTypes={
            editionUpdateStore.userAttachmentsColumnTypes
          }
          userAttachmentsOnRestore={(id: string) =>
            editionUpdateStore.userAttachmentsRestore(id)
          }
          userAttachmentsOnCommitChanges={(changes) =>
            editionUpdateStore.userAttachmentsCommitChanges(changes)
          }
          userAttachmentsOnCommitInlineChange={(rowId, changes) =>
            editionUpdateStore.userAttachmentsCommitInlineChange(rowId, changes)
          }
          userAttachmentsValidateChanges={(changes) =>
            editionUpdateStore.userAttachmentsValidateChanges(changes)
          }
          userAttachmentsRows={
            editionUpdateStore.entityValues.showUserAttachments
          }
          userAttachmentsError={
            editionUpdateStore.errors.userAttachments?.message
          }
          createdAt_={editionUpdateStore.getCreatedAt_Value()}
          lastUpdatedAt_={editionUpdateStore.getLastUpdatedAt_Value()}
          dummyCRUDRestoredObservable={
            editionUpdateStore.dummyCRUDRestoredObservable
          }
          recordId={editionUpdateStore.editionInstance?.id || ""}
          showToolbox={showToolbox}
          showSaveFooter={showSaveFooter}
          saving={editionUpdateStore.saveDisabled}
          onSave={() => editionUpdateStore.saveEdition()}
          onCancel={() => editionUpdateStore.cancelEdition()}
          worksWithAuthors={editionUpdateStore.worksWithAuthors}
          viewFormWorks={(id: string) => editionUpdateStore.viewFormWorks(id)}
        />
        <ErrorDialog
          open={editionUpdateStore.errorDialogOpened}
          title={editionUpdateStore.errorTitle}
          description={editionUpdateStore.errorDescription}
          onClose={() => editionUpdateStore.closeErrorDialog()}
        />
        <ConfirmCancelDialog
          open={editionUpdateStore.confirmDialogOpened}
          title={editionUpdateStore.confirmDialogTitle}
          description={editionUpdateStore.confirmDialogDescription}
          onConfirm={() => editionUpdateStore.executeConfirmedAction()}
          onCancel={() => editionUpdateStore.closeConfirmDialog()}
        />
      </>
    );
  }
);
